import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ChatComponent from './chat';
import { useT } from './i18n';
import './styles/ChatPage.css';

const ChatPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const t = useT();

  // Get agent info from navigation state
  const agentType = location.state?.agentType || 'default';
  const initialPrompt = location.state?.initialPrompt || '';
  
  // Set agent name and welcome message based on type
  const getAgentInfo = () => {
    const welcomeMsg = t.chat.welcome;
    switch(agentType) {
      case 'vibrancy':
        return { name: 'Avernano', welcomeMsg };
      case 'conscientiousness':
        return { name: 'Idomedes', welcomeMsg };
      case 'civility':
        return { name: 'Anatanos', welcomeMsg };
      case 'artificiality':
        return { name: 'Kalaris', welcomeMsg };
      default:
        return { name: t.chat.defaultAgentName, welcomeMsg };
    }
  };

  const agentInfo = getAgentInfo();
  
  const [messages, setMessages] = useState([
    {
      id: '1',
      text: agentInfo.welcomeMsg,
      user: {
        id: 'bot',
        name: agentInfo.name,
      },
      timestamp: new Date(),
    }
  ]);

  const [isTyping, setIsTyping] = useState(false);

  const handleSendMessage = async (message) => {
    // First, add the user message to the conversation immediately
    setMessages(prevMessages => [...prevMessages, message]);
    
    // Show typing indicator
    setIsTyping(true);

    try {
      // Use your backend endpoint
      const backendUrl = process.env.REACT_APP_BACKEND_ADDRESS || "http://localhost:5000";
      console.log('Sending message to backend:', backendUrl);
      
      // Build conversation history for better context (including the new message)
      let conversationHistory = [...messages, message].map(msg => ({
        role: msg.user.id === 'bot' ? 'agent' : 'user',
        content: msg.text
      }));

      // Add initial system prompt if available
      if (initialPrompt && initialPrompt.trim()) {
        console.log('Adding initial prompt to conversation history: ', initialPrompt);
        conversationHistory = [
          { role: 'system', content: initialPrompt },
          ...conversationHistory
        ];
      }

      // Send message to your backend
      const response = await fetch(`${backendUrl}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messages: conversationHistory
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('Backend response:', data);
        
        // Clean up any <think> tags from the response
        let cleanedMessage = data.message;
        const thinkTagStart = cleanedMessage.indexOf('<think>');
        const thinkTagEnd = cleanedMessage.indexOf('</think>');
        if (thinkTagStart !== -1 && thinkTagEnd !== -1 && thinkTagEnd > thinkTagStart) {
          cleanedMessage = cleanedMessage.slice(0, thinkTagStart) + cleanedMessage.slice(thinkTagEnd + 8);
        }

        // Add bot response to messages
        const botMessage = {
          id: Date.now().toString(),
          text: cleanedMessage,
          user: {
            id: 'bot',
            name: agentInfo.name,
          },
          timestamp: new Date(),
        };

        setMessages(prevMessages => [...prevMessages, botMessage]);
        console.log('Added bot message:', botMessage);
        
        // Hide typing indicator
        setIsTyping(false);
      } else {
        console.log('Backend error:', data);
        setIsTyping(false); // Hide typing indicator on error
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error sending message to backend:', error);
      
      // Hide typing indicator
      setIsTyping(false);
      
      // Add error response
      const errorMessage = {
        id: Date.now().toString(),
        text: error.message.includes('fetch')
          ? t.chat.connectionError
          : t.chat.genericError(error.message),
        user: {
          id: 'bot',
          name: 'System',
        },
        timestamp: new Date(),
      };

      setMessages(prevMessages => [...prevMessages, errorMessage]);
    }
  };

  const goBackToHome = () => {
    navigate('/chat-selection');
  };

  return (
    <div className="chat-page">
      <div className="chat-page-header">
        <button 
          className="back-button"
          onClick={goBackToHome}
        >
          {t.chat.back}
        </button>
        <h2>{agentInfo.name} {t.chat.headerSuffix}</h2>
      </div>
      
      <div className="chat-container">
        <ChatComponent
          theme='#6ea9d7'
          currentUserId="user"
          messages={messages}
          onSendMessage={handleSendMessage}
          placeholder={t.chat.placeholder(agentInfo.name)}
          headerTitle={`${agentInfo.name}`}
          showTypingIndicator={isTyping}
          typingIndicatorContent={t.chat.typing(agentInfo.name)}
        />
      </div>
    </div>
  );
};

export default ChatPage;